import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridRowSelectionModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro'
import cn from 'classnames'
import { base_colorContentBorder } from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { LocationState } from 'history'
import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import LoadingCell from 'src/components/DataGrid/cells/LoadingCell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotificationItem from 'src/components/NotificationItem/NotificationItem'
import { useTranslate } from 'src/localization/Trns'
import { NotificationListResponse } from 'src/models/notifications.models'
import { activityUrl } from 'src/navigation/urls'
import { useFetchNotifications } from 'src/queries/activity.queries'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'

import style, { NoNotificationsMessageStyle } from './NotificationsList.style'

interface NotificationDataGridRowModel extends GridValidRowModel {
  notification: NotificationListResponse
  index: number
}

interface LoadingRowModel extends GridValidRowModel {}

type NotificationDataGridColumnType = Array<
  GridColDef<NotificationDataGridRowModel | LoadingRowModel>
>

const NotificationsList = (): React.ReactElement => {
  const translate = useTranslate()

  const { data, fetchNextPage, isLoading, isFetching, isFetchingNextPage } =
    useFetchNotifications()

  // Sets row selection when the component mounts
  // so that the first task is visually selected
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({ type: 'include', ids: new Set<string>() })

  const history = useHistory<LocationState>()
  const location = useSelector(selectLocationFromState)

  useEffect(() => {
    if (location.pathname !== activityUrl()) {
      return
    }

    const params = queryString.parse(location.search)
    if (params.tab !== 'notifications') {
      return
    }
    if (params.id === undefined && data?.notifications[0]?.id !== undefined) {
      history.replace({
        pathname: activityUrl(),
        search: `?tab=notifications&id=${data.notifications[0].id}`,
      })
    }
    if (typeof params.id === 'string') {
      setRowSelectionModel({
        type: 'include',
        ids: new Set([params.id]),
      })
    }
  }, [location.search, data?.notifications[0]?.id])

  const NoNotificationsMessage = (): React.ReactElement => (
    <div css={NoNotificationsMessageStyle}>
      {translate('No notifications on your plate. Enjoy the moment!')}
    </div>
  )

  const loadingColumns: NotificationDataGridColumnType = [
    {
      field: 'notification',
      headerName: translate('Notification'),
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params): React.ReactElement => {
        return <LoadingCell params={params} />
      },
      renderHeader: () => <></>,
    },
  ]

  const loadingRows = Array.from({ length: 7 }, (_, i) => ({
    id: `loading-${i}`,
  }))

  const columns: NotificationDataGridColumnType = [
    {
      field: 'notification',
      headerName: translate('Notification'),
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params): React.ReactElement => {
        const notification = params.row.notification
        return (
          <NotificationItem key={notification.id} notification={notification} />
        )
      },
      renderHeader: () => <></>,
    },
  ]

  const columnsSafe = useMemo(
    () =>
      columns.filter(
        (column) => column !== undefined,
      ) as NotificationDataGridColumnType,
    [columns],
  )

  const rows = (data?.notifications ?? []).map((notification, i) => {
    return {
      id: notification.id,
      notification: notification,
      index: i,
    }
  })

  // handle infinite scroll
  const handleOnRowsScrollEnd = useCallback<
    NonNullable<DataGridProProps['onRowsScrollEnd']>
  >((): void => {
    const complete =
      data?.pagination.numberOfPages !== undefined &&
      data?.pagination.page !== undefined &&
      data?.pagination.numberOfPages === data?.pagination.page + 1 // +1 for 0-indexing
    if (!isFetching && !complete) {
      fetchNextPage()
    }
  }, [fetchNextPage, isFetching, data?.pagination])

  return (
    <div className={cn('NotificationsList')} css={style}>
      <DataGridPro
        rows={isLoading ? loadingRows : rows}
        columns={isLoading ? loadingColumns : columnsSafe}
        columnHeaderHeight={0}
        rowHeight={80}
        onRowsScrollEnd={handleOnRowsScrollEnd}
        rowSelectionModel={rowSelectionModel}
        sx={{
          border: 'none', // Remove border
          borderRadius: 0, // Remove border radius
          '& .MuiDataGrid-cell': {
            border: 'none',
          },
          // Add a partial separator line between rows
          '& .MuiDataGrid-row': {
            position: 'relative',
          },
          '& .MuiDataGrid-row:not(:first-of-type)::after': {
            content: '""',
            position: 'absolute',
            top: '-1px',
            left: spacing(5),
            right: spacing(5),
            height: '1px',
            background: base_colorContentBorder,
          },
          '.MuiDataGrid-cell:focus': {
            outline: 'none', // Remove focus outline
          },
          // Lower the scrollbar so it is below modals
          '.MuiDataGrid-scrollbar': {
            zIndex: 1,
          },
        }}
        loading={isFetchingNextPage}
        slots={{
          columnHeaders: () => null, // Removes header render
          loadingOverlay: () => <LoadingBackgroundPage />,
          noResultsOverlay: () => <NoNotificationsMessage />,
          noRowsOverlay: () => <NoNotificationsMessage />,
        }}
        hideFooter
        onRowClick={(params): void => {
          const notification = params.row.notification
          history.replace({
            pathname: activityUrl(),
            search: `?tab=notifications&id=${notification.id}`,
          })
        }}
      />
    </div>
  )
}

export default NotificationsList
